import type { ReadonlySignal } from '@preact/signals-react';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { ReactContainer } from 'src/ReactContainer';
import type { WidgetLocation } from 'src/types/WidgetLocation';
import { z } from 'zod';

export const widgetIdSchema = z.string().brand<'WidgetId'>();
export type WidgetId = z.infer<typeof widgetIdSchema>;

export const containerIdSchema = z.string().brand<'ContainerId'>();
export type ContainerId = z.infer<typeof containerIdSchema>;

export const windowIdSchema = z.string().brand<'WindowId'>();
export type WindowId = z.infer<typeof windowIdSchema>;

/**
 * Closure that returns the initial persisted state for a widget instance.
 *
 * Synchronous by contract: the caller of `Container.addInitialWidget` must
 * already hold the state (web reads its in-memory ledger; native windows
 * receive it in the window-init payload or the instantiate IPC message), so
 * widget construction never awaits a transport. This keeps registration —
 * and entitlement-driven placeholder/real swaps — a single synchronous
 * operation. See RFC 0008 §Widget initialization via `GetInitialData`.
 */
export type GetInitialData<T> = () => T;

export interface SelfOwnedWidget<TState> {
    toJSON(): TState;
    readonly dirty: ReadonlySignal<boolean>;
    markClean(): void;
}

export interface SelfOwnedWidgetClass<TState, TModel extends SelfOwnedWidget<TState>> {
    readonly stateSchema: z.ZodType<TState>;
    readonly initialStateSchema?: z.ZodType<unknown>;
    defaultState(): TState;
    defaultInitialState?(): unknown;
    fromJSON(container: ReactContainer): TModel;
}

export interface LedgerEntry<TState> {
    id: WidgetId;
    type: WidgetType;
    location: WidgetLocation;
    windowId: WindowId;
    persistedState: TState;
}

// `any` is load-bearing: `SelfOwnedWidgetClass<unknown, ...>` rejects concrete
// widget classes because their `stateSchema` types are invariant.
export type AnySelfOwnedWidgetClass = SelfOwnedWidgetClass<any, SelfOwnedWidget<any>>;
